import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { registrationSchema } from "@shared/utils/yupSchema";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup.js";
import { CustomTextField } from "@shared/components/CustomTextField";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { signup } from "@store/slices/authSlice";
import PasswordStrengthMeter from "@shared/components/PasswordStrengthMeter";

// Email + password only. Username is derived server-side from the
// email local-part (the same fallback OAuth sign-ups use), and full
// name is edited on the profile page — keeping this form the same
// weight as the "Continue with Google" alternative beside it.
interface MyForm {
  email: string;
  password: string;
}

const SignUpForm = () => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state) => state.auth.isLoading);
  // The show-password toggle is what makes a single password field
  // sound practice (per NIST 800-63B): seeing what you typed catches
  // typos better than typing it twice did.
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<MyForm>({
    resolver: yupResolver(registrationSchema),
  });
  // Subscribe to password + email so the strength meter updates as the
  // user types and zxcvbn treats their own address as guessable input.
  // watch()'s per-keystroke re-render is fine for a form this small.
  const passwordValue = watch("password") ?? "";
  const userInputs = [watch("email")];

  const submitForm = (data: MyForm) => {
    if (isLoading) return;
    dispatch(signup(data));
  };

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(submitForm)}
      noValidate
      spacing={2}
      sx={{ pt: 1 }}
    >
      <CustomTextField
        {...register("email")}
        autoFocus
        autoComplete="email"
        name="email"
        label="Email"
        type="email"
        disabled={isLoading}
        error={!!errors.email}
        helperText={errors.email?.message ?? " "}
        fullWidth
      />
      <CustomTextField
        {...register("password")}
        autoComplete="new-password"
        name="password"
        label="Password"
        type={showPassword ? "text" : "password"}
        disabled={isLoading}
        error={!!errors.password}
        helperText={errors.password?.message ?? "At least 12 characters."}
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((v) => !v)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <PasswordStrengthMeter password={passwordValue} userInputs={userInputs} />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={isLoading}
        startIcon={
          isLoading ? <CircularProgress size={18} color="inherit" /> : null
        }
      >
        {isLoading ? "Creating account…" : "Create account"}
      </Button>
    </Stack>
  );
};

export default SignUpForm;
